import React, { useState, useRef, useEffect } from "react";
import { Bell, UserRound, UserCog, Power, ChevronDown } from "lucide-react";
import Busca from "../../pages/busca/Busca";
import ModalSearch from "../../components/modal-search/ModalSearch";
import Search from "../../components/search/Search";
import SidebarLandingPage from "../../components/sidebar-dashboard/SidebarLandingPage";
import { useLocation } from "react-router-dom";
import { Link, useNavigate } from "react-router-dom";
import logoHeader from "../../assets/logo-header.svg";
import {
  saveToGlobalState,
  getStoredData,
  removeSpecificData,
} from "../../utils/localStorage";
import { useAuth } from "../../context/AuthContext";
import GoogleLoginButton from "../../components/googleLoginButton/GoogleLoginButton";
import FacebookLoginButton from "../../components/facebookLoginButton/FacebookLoginButton";
import UseLogin from "../../hooks/UseLogin/UseLogin";
import UseNotifications from "../../hooks/UseNotifications/UseNotifications";
import UseClientTripsNegotiations from "../../hooks/UseTripsNegotiations/UseClientTripsNegotiations";
import UseTravelerTripsNegotiations from "../../hooks/UseTripsNegotiations/UseTravelerTripsNegotiations";
import UseReceiverTripsNegotiations from "../../hooks/UseTripsNegotiations/UseReceiverTripsNegotiations";

const API_URL = process.env.REACT_APP_API_URL;
export default function Header({ menuOpen, setMenuOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const triggerRef = useRef(null);

  const [showLocationRequiredModal, setShowLocationRequiredModal] =
    useState(false);
  const [locationPermissionState, setLocationPermissionState] =
    useState("prompt"); // granted | denied | prompt | unsupported
  const [menuOpenLandingPage, setMenuOpenLandingPage] = useState(false);
  const [menuOpenMain, setMenuOpenMain] = useState(false);
  const menuRef = useRef(null);
  const [activeTab, setActiveTab] = useState("");
  const [lockedTab, setLockedTab] = useState(null);

  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth < 935);

  const { handleSearchClick, RenderModal } = ModalSearch();

  const {
    showLoginModal,
    setShowLoginModal,
    user,
    setUser,
    logout,
    stopUserLocation,
    accessToken,
  } = useAuth();
  const {
    email,
    setEmail,
    senha,
    setSenha,
    loading,
    error,
    setError,
    handleLogin,
  } = UseLogin({
    setUser,
    setShowLoginModal,
    navigate,
  });

  const {
    notificationsOpen,
    setNotificationsOpen,

    notifications,

    loadingNotifications,

    confirmDeleteNotifications,
    setConfirmDeleteNotifications,
    handleNotifications,
    removeNotification,
  } = UseNotifications();

  const { tripsNegotiations: tripsNegotiationsClient } =
    UseClientTripsNegotiations();

  const { tripsNegotiations: tripsNegotiationsTraveler } =
    UseTravelerTripsNegotiations();

  const { tripsNegotiations: tripsNegotiationsReceiver } =
    UseReceiverTripsNegotiations();

  const specialRoutes = [
    "/dashboard-receiver",
    "/trips-progress-receiver",
    "/trips-concluded-receiver",
    "/dashboard-client",
    "/trips-progress-client",
    "/trips-concluded-client",
    "/payment-client",
    "/dashboard-traveler",
    "/trips-progress-traveler",
    "/trips-concluded-traveler",
    "/payment-traveler",
  ];

  const isSpecialRoute = specialRoutes.includes(location.pathname);

  const handleAccountClick = () => {
    const dataSg = getStoredData();
    if (user?.serie) {
      if (dataSg?.tab_login == "CLIENTE") {
        navigate("/dashboard-client");
        return;
      } else if (dataSg?.tab_login == "VIAJANTE") {
        navigate("/dashboard-traveler");
        return;
      } else if (dataSg?.tab_login == "DESTINATÁRIO") {
        navigate("/dashboard-receiver");
        return;
      } else if (dataSg?.tab_login == "MALA") {
        navigate("/dashboard-traveler");
        return;
      } else if (dataSg?.tab_login == "COMPRAR") {
        navigate("/dashboard-client");
        return;
      }
      navigate("/dashboard-client");
    } else {
      saveToGlobalState({
        tab_login: "CLIENTE",
      });

      setLockedTab(null); // libera todas
      setActiveTab("CLIENTE");
      removeSpecificData("payload");
      removeSpecificData("confirmation_token");
      setShowLoginModal(true);
    }
  };

  const travelerTrips = tripsNegotiationsTraveler || [];

  const hasTravelStatus7 = travelerTrips.some(
    (trip) => String(trip.trip_status) === "7",
  );

  const checkLocationPermission = async () => {
    if (!hasTravelStatus7 || !user) {
      setShowLocationRequiredModal(false);
      return;
    }

    if (!navigator.geolocation) {
      setLocationPermissionState("unsupported");
      setShowLocationRequiredModal(true);
      return;
    }

    try {
      // Se o browser suportar Permissions API
      if (navigator.permissions?.query) {
        const permission = await navigator.permissions.query({
          name: "geolocation",
        });

        setLocationPermissionState(permission.state);

        if (permission.state !== "granted") {
          setShowLocationRequiredModal(true);
        } else {
          setShowLocationRequiredModal(false);
        }

        // fica ouvindo mudança de permissão
        permission.onchange = () => {
          setLocationPermissionState(permission.state);

          if (hasTravelStatus7 && permission.state !== "granted") {
            setShowLocationRequiredModal(true);
          } else {
            setShowLocationRequiredModal(false);
          }
        };

        return;
      }

      // fallback para browsers sem Permissions API
      navigator.geolocation.getCurrentPosition(
        () => {
          setLocationPermissionState("granted");
          setShowLocationRequiredModal(false);
        },
        () => {
          setLocationPermissionState("denied");
          setShowLocationRequiredModal(true);
        },
        { enableHighAccuracy: true, timeout: 10000 },
      );
    } catch (error) {
      console.error("Erro ao verificar permissão de localização:", error);
      setLocationPermissionState("prompt");
      setShowLocationRequiredModal(true);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Se o gatilho NÃO está visível, significa que rolamos para baixo
        setScrolled(!entry.isIntersecting);
      },
      { threshold: [0] },
    );

    if (triggerRef.current) {
      observer.observe(triggerRef.current);
    }

    return () => {
      if (triggerRef.current) {
        observer.unobserve(triggerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    if (hasTravelStatus7) {
      checkLocationPermission();
    } else {
      setShowLocationRequiredModal(false);
    }
  }, [user, hasTravelStatus7]);

  useEffect(() => {
    const sendLocation = () => {
      if (!user || !hasTravelStatus7) return;

      if (!navigator.geolocation) {
        setLocationPermissionState("unsupported");
        setShowLocationRequiredModal(true);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setLocationPermissionState("granted");
          setShowLocationRequiredModal(false);

          if (!accessToken) return;

          try {
            await fetch(`${API_URL}/api/auth/update-location/`, {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ lat, lng }),
            });

            console.log("Localização cadastrada com sucesso!");
          } catch (err) {
            console.error("Erro no envio do GPS:", err);
          }
        },
        (error) => {
          console.error("Erro ao ler GPS:", error);

          // Se existe viagem status 7 e não conseguiu obter localização,
          // mantém a modal aberta
          if (hasTravelStatus7) {
            setLocationPermissionState("denied");
            setShowLocationRequiredModal(true);
          }
        },
        { enableHighAccuracy: true, timeout: 10000 },
      );
    };

    if (hasTravelStatus7) {
      sendLocation();
      const interval = setInterval(sendLocation, 10000);
      return () => clearInterval(interval);
    }
  }, [user, accessToken, hasTravelStatus7]);

  useEffect(() => {
    const haveDataListLocation =
      tripsNegotiationsTraveler && tripsNegotiationsTraveler.length > 0;

    if (user && haveDataListLocation) {
      const hasTravelStatus7 = tripsNegotiationsTraveler.some(
        (trip) => String(trip.trip_status) === "7",
      );

      if (!hasTravelStatus7 && typeof stopUserLocation === "function") {
        console.log("Listas validadas e sem status 7. Desligando GPS...");
        stopUserLocation();
      }
    }
  }, [user, tripsNegotiationsTraveler, stopUserLocation]);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth < 935);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (showLoginModal) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
      document.body.style.overflow = "unset";
    }

    const dataStorage = getStoredData();
    const tabMap = {
      MALA: "VIAJANTE",
      COMPRAR: "CLIENTE",
    };

    const targetTab = tabMap[dataStorage?.tab_login] || dataStorage?.tab_login;

    setActiveTab(targetTab);

    setLockedTab(dataStorage?.tab_login === "CLIENTE" ? null : targetTab);

    return () => {
      document.documentElement.style.overflow = "auto";
      document.body.style.overflow = "unset";
    };
  }, [showLoginModal]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setTimeout(() => {
          setMenuOpenMain(false);
        }, 500);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();

    setMenuOpenMain(false);

    window.location.href = "/";
  };

  const handleStorage = () => {
    removeSpecificData("qual_meio_transporte");

    if (user?.serie) {
      saveToGlobalState({
        voltando_trajeto: "nao",
      });

      navigate("/qual-meio-transporte");
    } else {
      saveToGlobalState({
        voltando_trajeto: "nao",
        tab_login: "MALA",
      });

      setLockedTab("VIAJANTE"); // bloqueia nas tabs
      setActiveTab("VIAJANTE");
      setShowLoginModal(true);
    }
  };

  const unreadNotifications = notifications.filter((n) => !n.viewed).length;

  return (
    <>
      <style>{`
       :root {
            --page-width: 948px;
        }

         * {
          box-sizing: border-box;
        }
        
        #root {
          overflow-x: clip; /* Esconde o que vazar para os lados SEM quebrar o sticky */
          overflow-y: visible; /* Garante que o scroll vertical continue normal */
        }

        .page {
          width: var(--page-width);
          max-width: calc(100% - 32px);
          margin: 0 auto;
        }
        
        .header {
            position: sticky;
            top: 0;
            z-index: 20;
            background: transparent;
            transition: background 0.3s ease;
        }

        .header.scrolled {
            background: #fff !important;
        }

        .nav {
          height: 84px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
        }

        .brand {
          display: flex;
          align-items: center;
          gap: 10px;
          height: 100px;
          overflow: hidden;
          font-weight: 800;
          letter-spacing: -0.03em;
          font-size: 26px;
          color: #2d8cff;
        }

        .brand span {
          color: #25c6ca;
        }

        .nav-links {
          display: flex;
          align-items: right;
          gap: 28px;
          font-size: 15px;
          color: #242a35;
          margin-left: auto;
        }

        .nav-links a {
          color: inherit;
          font-weight: 800;
          text-decoration: none;
          opacity: 0.9;
        }

        .nav-links a:hover {
          opacity: 1;
          color: #0ed079ff 
        }

        .blink-shadow {
          animation: blinkShadow 0.6s ease-in-out 3;
        }

        @keyframes blinkShadow {
          0% {
            box-shadow: 0 0 0px rgba(14, 208, 121, 0);
          }
          50% {
            box-shadow: 0 0 20px rgba(14, 208, 121, 0.9);
          }
          100% {
            box-shadow: 0 0 0px rgba(14, 208, 121, 0);
          }
        }

        .account-wrapper {
            position: relative;
            display: inline-block;
          }

          .account {
            display: inline-flex;
            align-items: center;
            gap: 12px;
            height: 48px;
            padding: 0 16px 0 8px;
            margin-right: calc(-51vw + 1%);
            padding-right: calc(50vw);
            background: linear-gradient(
              90deg,
              #1a7fe7 0%,
              #19c4c9 10%
            );
            color: #fff;
            border-radius: 999px 0 0 999px;
            font-weight: 700;
            font-size: 14px;
            z-index: 1;
            cursor: pointer;
            user-select: none;
          }

          .account:hover {
            filter: brightness(1.09);
          }

          .profile-container {
            position: relative; 
            display: inline-block;
          }

          .profile-img {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            display: flex;            
            align-items: center;
            justify-content: center;
            border: 2px solid rgba(255, 255, 255, 0.22);
            flex-shrink: 0;
            object-fit: cover;
          }

          .notifications-badge {
            position: absolute;
            top: -8px; 
            right: -8px;
            
            min-width: 20px;
            height: 20px;
            padding: 0 4px;
            border-radius: 10px;
            
            background-color: #ae0b42; 
            color: white;
            font-size: 12px;
            font-weight: 700;
            
            display: flex;
            align-items: center;
            justify-content: center;
            
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.6);
            
            white-space: nowrap;
          }

          
          .account-icon {
            font-size: 35px;
          }

          .account-title {
            white-space: nowrap;
            line-height: 1;
          }

          .account-chevron {
            opacity: 0.9;
          }

          .account-menu-pc {
            position: relative;
            top: 105px;
            right: 140px;
            margin-right: -232px;
            min-width: 220px;
            max-width: calc(100vw - 24px);
            background: rgba(255, 255, 255, 0.98);
            backdrop-filter: blur(14px);
            border: 1px solid rgba(0, 0, 0, 0.06);
            border-radius: 18px;
            box-shadow: 0 18px 45px rgba(0, 0, 0, 0.12);
            padding: 8px;
            z-index: 999;
            animation: dropdownIn 0.18s ease-out;
          }

          .account-menu-mobile{
            position: fixed;
            top: 73px;
            right: 20px;
            min-width: 220px;
            max-width: calc(100vw - 24px);
            background: rgba(255, 255, 255, 0.98);
            backdrop-filter: blur(14px);
            border: 1px solid rgba(0, 0, 0, 0.06);
            border-radius: 18px;
            box-shadow: 0 18px 45px rgba(0, 0, 0, 0.12);
            padding: 8px;
            z-index: 999;
            animation: dropdownIn 0.18s ease-out;
          }

          .icon-wrapper {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .icon-wrapper .notifications-badge {
            position: absolute;
            top: -6px;   
            right: -6px; 

            background-color: #ae0b42; 
            color: white;
            min-width: 16px;
            height: 16px;
            font-size: 10px;
            font-weight: 700;
            border-radius: 999px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid white; 
            white-space: nowrap;
            box-shadow: 0 1px 3px rgba(0,0,0,0.15);
          }

          .account-menu-item {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 12px;
            border: none;
            background: transparent;
            padding: 12px 14px;
            border-radius: 12px;
            color: #242a35;
            font-weight: 600;
            font-size: 14px;
            cursor: pointer;
            text-align: left;
            transition: background 0.2s ease, transform 0.2s ease;
          }

          .account-menu-item:hover {
            background: #f3f6fb;
            transform: translateX(2px);
          }

          .account-menu-item span {
            font-family: "Gotham"
          }

          .account-menu-item.logout {
            color: #d64545;
          }

          .account-menu-item.logout:hover {
            background: rgba(214, 69, 69, 0.08);
          }

          @keyframes dropdownIn {
            from {
              opacity: 0;
              transform: translateY(-6px) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          /* NOTIFICATION DRAWER */

          .notifications-overlay {
            position: fixed;
            inset: 0;
            background: rgba(8, 15, 25, 0.18);
            backdrop-filter: blur(4px);
            z-index: 9999;

            animation: fadeOverlay .25s ease;
          }

          .notifications-modal {
            position: absolute;

            top: 20px;
            left: 20px;

            width: 420px;
            max-width: calc(100vw - 24px);

            max-height: calc(100vh - 40px);

            background:
              linear-gradient(
                180deg,
                rgba(255,255,255,.98),
                rgba(247,250,255,.95)
              );

            border-radius: 30px;

            overflow: hidden;

            box-shadow:
              0 40px 80px rgba(0,0,0,.15),
              inset 0 1px rgba(255,255,255,.7);

            animation: slideLeft .32s ease;
          }

          .notifications-header {
            padding: 24px;

            background:
              linear-gradient(
                135deg,
                #1a7fe7,
                #19c4c9 40%
              );

            color: white;
          }

          .notifications-title {
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

          .notifications-title h2 {
            margin: 0;
            font-size: 22px;
          }

          .notifications-list {
            padding: 12px;

            max-height: 72vh;

            overflow-y: auto;
          }

          .notifications-item {
            display: flex;

            gap: 14px;

            padding: 18px;

            border-radius: 20px;

            cursor: pointer;

            transition: .25s;

            margin-bottom: 10px;
          }

          .notifications-item:hover {
            background: #eef8ff;

            transform: translateX(4px);
          }

          .notifications-dot {
            width: 12px;
            height: 12px;

            border-radius: 50%;

            background:
              linear-gradient(
                135deg,
                #1a7fe7,
                #0ed079
              );

            margin-top: 8px;

            flex-shrink: 0;
          }

          .notifications-content h4 {
            margin: 0 0 6px;

            color: #242a35;

            font-size: 15px;
          }

          .notifications-content p {
            margin: 0;

            color: #64748b;

            font-size: 13px;

            line-height: 1.5;
          }

          .notifications-time {
            margin-top: 8px;

            font-size: 12px;

            color: #9aa6b2;
          }

          .notifications-empty {
            padding: 70px 20px;

            text-align: center;

            color: #9aa6b2;
          }

          .close-notifications {
            position: absolute;

            right: 18px;
            top: 18px;

            width: 40px;
            height: 40px;

            border: none;

            border-radius: 50%;

            background: rgba(255,255,255,.2);

            color: white;

            cursor: pointer;
          }

          .close-notifications:hover {
            background: rgba(255,255,255,.35);
          }

          @keyframes slideLeft {
            from {
              transform:
                translateX(-50px)
                scale(.98);

              opacity: 0;
            }

            to {
              transform:
                translateX(0)
                scale(1);

              opacity: 1;
            }
          }

          @keyframes fadeOverlay {
            from { opacity:0 }
            to { opacity:1 }
          }

          .notification-actions {
            margin-top: 14px;
            width: 150px;
          }

          .remove-btn {
            border: none;

            background: rgba(239, 68, 68, 0.08);

            color: #dc2626;

            padding: 8px 14px;

            border-radius: 999px;

            font-size: 12px;

            font-weight: 700;

            cursor: pointer;

            transition: .25s;
          }

          .remove-btn:hover {
            background: rgba(239,68,68,.15);
          }

          .confirm-remove {
            margin-top: 14px;

            background: #fff6f6;

            border: 1px solid #ffd2d2;

            padding: 14px;

            width: 300px;

            border-radius: 16px;
          }

          .confirm-remove-text {
            color: #7f1d1d;

            font-size: 13px;

            margin-bottom: 12px;

            font-weight: 600;
          }

          .confirm-buttons {
            display: flex;

            gap: 10px;
          }

          .confirm-btn {
            flex: 1;

            border: none;

            height: 40px;

            border-radius: 12px;

            cursor: pointer;

            font-weight: 700;
          }

          .confirm-btn.cancel {
            background: #eef2f7;

            color: #475569;
          }

          .confirm-btn.delete {
            background:
              linear-gradient(
                135deg,
                #ef4444,
                #dc2626
              );

            color: white;
          }

          .confirm-btn:hover {
            transform: translateY(-1px);
          }

          @media (max-width:768px){
            .notifications-modal{
              left:12px;
              right:12px;
              width:auto;
              top:12px;
            }
          }

         @media (max-width: 935px) {
            :root {
              --page-width: 100%;
            }

            .nav {
            height: 72px;
          }

          .nav-links {
            display: none;
          }

          .account-title {
            display: none;
          }

          .account {
            margin-right: calc(-50vw + 0%);
            background: linear-gradient(
            90deg,
            #1a7fe7 0%,
            #19c4c9 20%
          )
          }         
         }

         @media (min-width: 981px) and (max-width: 1199px)  {
          :root {
            --page-width: 948px;
            }
         }

         
         @media (min-width: 1200px) and (max-width: 1439px) {
          :root {
              --page-width: 1120px;
          }
        }

        @media (max-width: 1532px) { 
          .account-menu-mobile {
            display: block
          }

          .account-menu-pc {
            display: none
          }
        }

        @media (min-width: 1532px) { 
          .account-menu-pc {
            display: block
          }

          .account-menu-mobile {
            display: none
          }
        }

        @media (min-width: 1440px) {
          :root {
            --page-width: 1280px;
          }
        }

          .modal-overlay {
            position: fixed; top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(0, 0, 0, 0.4); display: flex; 
            justify-content: center; align-items: center; z-index: 1000;
          }

          .modal-card {
            background: #f1f3f5; 
            width: 80%; 
            max-width: 400px; 
            height: 60%; 
            padding: 20px; 
            border-radius: 30px; 
            text-align: center;
            box-sizing: content-box;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            overflow-y: auto;
            overflow-x: hidden;
            clip-path: inset(0 round 30px); 
          }

          .modal-card::-webkit-scrollbar {
            width: 8px;
          }

          .modal-card::-webkit-scrollbar-track {
            background: transparent;
            margin-top: 20px;   
            margin-bottom: 20px;
          }

          .modal-card::-webkit-scrollbar-thumb {
            background-color: #00bcd4;
            border-radius: 20px;
          }

          .modal-card h2 {
            color: #00bcd4;
          }

        .tab-container {
          display: flex; border: 1.5px solid #00bcd4; border-radius: 20px; border-right: none;
          overflow: hidden; margin-bottom: 25px; background: #fff;
        }
        
        .tab-btn {
          flex: 1; padding: 10px; border: none; background: transparent; border-radius: 20px;  border-right: 1.5px solid #00bcd4;
          color: #00bcd4; font-weight: bold; font-size: 11px; cursor: pointer;
        }

        .tab-btn:hover {
          background: rgba(25, 196, 201, 0.2)
        }

        .tab-btn.active {
          background: linear-gradient(
            90deg,
            #1a7fe7 0%,
            #19c4c9 40%,
            #0ed079ff 100%
          ); color: #fff;
        }

        .form-group { margin-bottom: 20px; }
        .form-group label { display: block; color: #00bcd4; font-weight: bold; margin-bottom: 8px; font-size: 18px; }
        .form-group input {
          color: #555; width: 100%; padding: 14px; border-radius: 30px; border: none;
          box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.1); outline: none; box-sizing: border-box;
        }

        .forgot-link { color: #999; font-size: 15px; text-decoration: none; display: block; margin-top: 15px; }

        .forgot-link:hover { text-decoration: underline; }

        .btn-submit {
          width: 100%; padding: 12px; border-radius: 30px; border: none;
          background: #007bff; color: #fff; font-size: 20px; font-weight: bold;
          cursor: pointer; margin-top: 5px;
        }
        .btn-submit:hover { background: #19c4c9 }

        .signup-link { color: #888; font-size: 16px; text-decoration: underline; display: block; margin: 15px 0; }

        .social-section h3 { font-size: 18px; margin: 20px 0 15px; }

        .social-btn {
          position: relative;
          width: 100%;
          height: 40px;
          padding: 0 16px;
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 30px;
          display: flex;
          align-items: center;
          justify-content: flex-start; 
          gap: 10px;
          cursor: pointer;
          color: #555;
          font-weight: 500;
        }

        .social-btn span {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          white-space: nowrap;
        }

        .social-btn:hover {
          border: 2px solid rgb(210 217 250 / 20%);
          background: rgb(210 217 250 / 20%)
        }
        .google-container {
            width: 100%;
            height: 52px; 
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            border-radius: 999px;
          }

          .facebook-login-button {
            display: flex;
            align-items: center;
            position: relative;
            width: 100%;
            height: 40px;
            background-color: #fff;
            color: #464545;
            border: none;
            border-radius: 999px;
            font-size: 15px;
            font-weight: 545;
            cursor: pointer;
          }

          .facebook-login-button:hover {
             border: 2px solid rgb(210 217 250 / 20%);
             background: rgb(210 217 250 / 20%)
          }

          .facebook-icon {
            width: 20px;
            height: 20px;
            fill: white;
            position: absolute;
            left: 16px;
          }

          .facebook-text {
            width: 100%;
            text-align: center;
          }


      `}</style>

      {isLargeScreen && (
        <SidebarLandingPage
          menuOpen={menuOpenLandingPage}
          setMenuOpen={setMenuOpenLandingPage}
          handleSearchClick={handleSearchClick}
          handleStorage={handleStorage}
        />
      )}

      <div
        ref={triggerRef}
        style={{ position: "absolute", top: 0, height: "1px", width: "100%" }}
      />

      <header className={scrolled ? "header scrolled" : "header"}>
        <div className="page nav">
          <div className="brand">
            <Link to="/">
              <img
                src={logoHeader}
                alt="Meu Ícone"
                style={{ marginTop: "15px", width: "70%", cursor: "pointer" }}
              />
            </Link>
          </div>
          <nav className="nav-links" aria-label="principal">
            <Link to="/como-funciona">Como funciona</Link>
            <Link to="/" onClick={(e) => handleSearchClick(e)}>
              Procurar mala
            </Link>
            <a href="#" onClick={handleStorage}>
              Oferecer mala
            </a>
            <Link to="/perguntas-frequentes">FAQ</Link>
          </nav>

          <div className="account-wrapper" ref={menuRef}>
            <div
              className="account"
              onClick={() =>
                user?.serie
                  ? setMenuOpenMain((prev) => !prev)
                  : handleAccountClick()
              }
            >
              <button
                style={{
                  position: "relative",
                  left: -20,
                  marginLeft: -50,
                  color: "#fff",
                }}
                className="mobile-menu-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    location.pathname === "/" ||
                    location.pathname === "/como-funciona" ||
                    location.pathname === "/busca" ||
                    location.pathname === "/perguntas-frequentes" ||
                    location.pathname === "/qual-meio-transporte" ||
                    location.pathname === "/my-profile" ||
                    location.pathname === "/aviao" ||
                    location.pathname === "/onibus" ||
                    location.pathname === "/carro" ||
                    location.pathname === "/moto" ||
                    location.pathname === "/trajeto" ||
                    location.pathname === "/data" ||
                    location.pathname === "/cidades-trajeto" ||
                    location.pathname === "/espaco-disponivel" ||
                    location.pathname === "/oferecer-espaco-na-volta" ||
                    location.pathname === "/trip-tracking-client" ||
                    location.pathname === "/trip-tracking-receiver"
                  ) {
                    setMenuOpenLandingPage(true);
                    return;
                  }
                  setMenuOpen((prev) => !prev);
                  setMenuOpenMain(false);
                }}
                aria-label="Abrir menu"
              >
                ☰
              </button>
              {user?.identificacao ? (
                <div className="profile-container">
                  <img
                    src={`${API_URL}${user.identificacao}`}
                    alt="Foto de perfil"
                    className="profile-img"
                  />

                  {unreadNotifications > 0 && (
                    <div className="notifications-badge">
                      {unreadNotifications}
                    </div>
                  )}
                </div>
              ) : (
                <span
                  style={{ paddingTop: 2 }}
                  className="material-icons account-icon"
                >
                  account_circle
                </span>
              )}
              <span className="account-title">
                {user?.nome?.split(" ")?.[0] || "Log In"}
              </span>
              {user?.serie && (
                <ChevronDown
                  size={16}
                  strokeWidth={2.2}
                  className="account-chevron"
                />
              )}
              {menuOpenMain && (
                <div
                  style={{
                    top:
                      isSpecialRoute || location.pathname === "/my-profile"
                        ? "105px"
                        : "128px",
                  }}
                  className="account-menu-pc"
                >
                  {!isSpecialRoute ? (
                    <button
                      className="account-menu-item"
                      onClick={handleAccountClick}
                    >
                      <UserRound size={20} strokeWidth={2.2} />
                      <span>Entrar no Painel</span>
                    </button>
                  ) : (
                    <></>
                  )}

                  {user?.serie && (
                    <>
                      {location.pathname !== "/my-profile" ? (
                        <button
                          className="account-menu-item"
                          onClick={() => {
                            window.location.href = "/my-profile";
                          }}
                        >
                          <UserCog size={20} strokeWidth={2.2} />
                          <span>Meu Perfil</span>
                        </button>
                      ) : (
                        <></>
                      )}
                      <button
                        className="account-menu-item"
                        onClick={handleNotifications}
                      >
                        <div className="icon-wrapper">
                          <Bell size={20} strokeWidth={2.2} />

                          {unreadNotifications > 0 && (
                            <div className="notifications-badge">
                              {unreadNotifications}
                            </div>
                          )}
                        </div>

                        <span>Notificações</span>
                      </button>

                      <button
                        className="account-menu-item logout"
                        onClick={handleLogout}
                      >
                        <Power size={20} strokeWidth={2.2} />
                        <span>Logout</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {menuOpenMain && (
            <div className="account-menu-mobile">
              {!isSpecialRoute ? (
                <button
                  className="account-menu-item"
                  onClick={handleAccountClick}
                >
                  <UserRound size={20} strokeWidth={2.2} />
                  <span>Entrar no Painel</span>
                </button>
              ) : (
                <></>
              )}

              {user?.serie && (
                <>
                  {location.pathname !== "/my-profile" ? (
                    <button
                      className="account-menu-item"
                      onClick={() => {
                        window.location.href = "/my-profile";
                      }}
                    >
                      <UserCog size={20} strokeWidth={2.2} />
                      <span>Meu Perfil</span>
                    </button>
                  ) : (
                    <></>
                  )}
                  <button
                    className="account-menu-item"
                    onClick={handleNotifications}
                  >
                    <div className="icon-wrapper">
                      <Bell size={20} strokeWidth={2.2} />

                      {unreadNotifications > 0 && (
                        <div className="notifications-badge">
                          {unreadNotifications}
                        </div>
                      )}
                    </div>

                    <span>Notificações</span>
                  </button>

                  <button
                    className="account-menu-item logout"
                    onClick={handleLogout}
                  >
                    <Power size={20} strokeWidth={2.2} />
                    <span>Logout</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      <RenderModal />

      {showLoginModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowLoginModal(false)}
          style={{
            alignItems: "flex-start",
            overflowY: "auto",
            padding: "20px 12px",
          }}
        >
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              height: "auto",
              maxHeight: "none",
              margin: "auto",
            }}
          >
            <h2 style={{ marginTop: 0 }}>Fazer login como</h2>

            <div className="tab-container">
              {["CLIENTE", "VIAJANTE", "DESTINATÁRIO"].map((tab) => {
                const isDisabled = lockedTab && lockedTab !== tab;

                return (
                  <button
                    key={tab}
                    className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;

                      setActiveTab(tab);

                      saveToGlobalState({
                        tab_login: tab,
                      });
                    }}
                    style={{
                      opacity: isDisabled ? 0.5 : 1,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                    }}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>

            {error && (
              <div
                style={{
                  backgroundColor: "#fde8e8",
                  color: "#c81e1e",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: "1px solid #f8b4b4",
                  fontSize: "14px",
                  marginTop: "12px",
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  fontWeight: "500",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  width: "95%",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                <span
                  style={{
                    marginRight: "10px",
                    display: "flex",
                    alignItems: "center",
                    color: "#e02424",
                  }}
                >
                  <svg
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    style={{ width: "20px", height: "20px" }}
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>

                {error}
              </div>
            )}

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError("");
                }}
              />
            </div>

            <div className="form-group">
              <label>Senha</label>
              <input
                type="password"
                value={senha}
                onChange={(e) => {
                  setSenha(e.target.value);
                  if (error) setError("");
                }}
              />
              <Link
                className="forgot-link"
                to="/esqueci-senha"
                onClick={() => setShowLoginModal(false)}
              >
                Esqueci a senha
              </Link>
            </div>

            <button
              className="btn-submit"
              onClick={handleLogin}
              disabled={loading}
            >
              {loading ? "Entrando..." : "Entrar"}
            </button>

            <Link
              className="signup-link"
              to="/cadastro"
              onClick={() => {
                localStorage.removeItem("google_prefill");
                setShowLoginModal(false);
              }}
            >
              Ainda não fiz o cadastro
            </Link>

            <div className="social-section">
              <h3>Criar conta nova</h3>
              <div className="google-btn-wrapper">
                <GoogleLoginButton
                  shape="pill"
                  size="large"
                  theme="outline"
                  text="continue_with"
                  width="100%"
                />
              </div>

              <FacebookLoginButton />
            </div>
          </div>
        </div>
      )}

      {notificationsOpen && (
        <div
          className="notifications-overlay"
          onClick={() => setNotificationsOpen(false)}
        >
          <div
            className="notifications-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="notifications-header">
              <button
                className="close-notifications"
                onClick={() => setNotificationsOpen(false)}
              >
                ✕
              </button>

              <div className="notifications-title">
                <h2>🔔 Notificações</h2>
              </div>

              <p style={{ marginTop: 8, opacity: 0.9 }}>
                Veja as últimas atualizações
              </p>
            </div>

            <div className="notifications-list">
              {loadingNotifications ? (
                <div className="notifications-empty">Carregando...</div>
              ) : notifications.length === 0 ? (
                <div className="notifications-empty">
                  🔕 Nenhuma notificação
                </div>
              ) : (
                notifications.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => {
                      sessionStorage.removeItem("payment_insurance");

                      if (
                        item.notification_type?.trim() === "insurance" ||
                        item.notification_type?.trim() ===
                          "receiver-selected-traveler" ||
                        item.notification_type?.trim() ===
                          "photo-confirmation" ||
                        (item.notification_type?.trim() === "chat" &&
                          item.notification_redirect?.trim() ===
                            "trips-progress-traveler")
                      ) {
                        tripsNegotiationsTraveler.map((trips) => {
                          if (
                            trips.serie_trip_negotiations ==
                            item.serie_trip_negotiations
                          )
                            navigate(`/${item.notification_redirect}`, {
                              state: {
                                item: trips,
                                notification_type: item.notification_type,
                                from: "notification",
                              },
                            });
                        });
                        return;
                      }

                      if (
                        item.notification_type?.trim() === "service" ||
                        item.notification_type?.trim() === "created-trip" ||
                        item.notification_type?.trim() ===
                          "receiver-selected-client" ||
                        item.notification_type?.trim() === "receiver-refused" ||
                        item.notification_type?.trim() ===
                          "photo-upload-client" ||
                        (item.notification_type?.trim() === "chat" &&
                          item.notification_redirect?.trim() ===
                            "trips-progress-client")
                      ) {
                        tripsNegotiationsClient.map((trips) => {
                          if (
                            trips.serie_trip_negotiations ==
                            item.serie_trip_negotiations
                          )
                            navigate(`/${item.notification_redirect}`, {
                              state: {
                                item: trips,
                                notification_type: item.notification_type,
                                from: "notification",
                              },
                            });
                        });
                        return;
                      }

                      if (
                        item.notification_type?.trim() === "receiver-removed" ||
                        item.notification_type?.trim() ===
                          "receiver-selected" ||
                        item.notification_type?.trim() ===
                          "photo-upload-receiver" ||
                        (item.notification_type?.trim() === "chat" &&
                          item.notification_redirect?.trim() ===
                            "trips-progress-receiver")
                      ) {
                        tripsNegotiationsReceiver.map((trips) => {
                          if (
                            trips.serie_trip_negotiations ==
                            item.serie_trip_negotiations
                          )
                            navigate(`/${item.notification_redirect}`, {
                              state: {
                                item: trips,
                                notification_type: item.notification_type,
                                from: "notification",
                              },
                            });
                        });
                        return;
                      }
                    }}
                    className="notifications-item"
                  >
                    <div className="notifications-dot" />

                    <div className="notifications-content">
                      <h4>{item.title}</h4>

                      <p>{item.message}</p>

                      <div className="notifications-time">{item.time}</div>

                      <div
                        className="notification-actions"
                        style={{ cursor: "default" }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        {confirmDeleteNotifications === item.id ? (
                          <div className="confirm-remove">
                            <div className="confirm-remove-text">
                              Tem certeza que deseja remover esta notificação?
                            </div>

                            <div className="confirm-buttons">
                              <button
                                className="confirm-btn cancel"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmDeleteNotifications(null);
                                }}
                              >
                                Cancelar
                              </button>

                              <button
                                className="confirm-btn delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeNotification(item.id);
                                }}
                              >
                                Remover
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            className="remove-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDeleteNotifications(item.id);
                            }}
                          >
                            Remover notificação
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {showLocationRequiredModal && hasTravelStatus7 && (
        <div
          className="modal-overlay"
          onClick={(e) => e.stopPropagation()} // impede fechar clicando fora
          style={{ zIndex: 10001 }}
        >
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "460px",
              height: "auto",
              borderRadius: "28px",
              textAlign: "center",
              padding: "28px 24px",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "10px" }}>📍</div>

            <h2 style={{ marginTop: 0, marginBottom: "12px" }}>
              Ative a sua localização
            </h2>

            <p
              style={{
                color: "#4b5563",
                fontSize: "15px",
                lineHeight: 1.6,
                marginBottom: "22px",
              }}
            >
              Você tem uma viagem em andamento. Para continuar acompanhando a
              viagem, é obrigatório manter a localização do dispositivo ativada
              e permitir o acesso à localização no navegador.
            </p>

            {locationPermissionState === "denied" && (
              <div
                style={{
                  background: "#fff4e5",
                  color: "#92400e",
                  border: "1px solid #fcd34d",
                  borderRadius: "14px",
                  padding: "12px 14px",
                  fontSize: "14px",
                  marginBottom: "18px",
                }}
              >
                A permissão de localização foi bloqueada ou o GPS do dispositivo
                está desligado. Ative a localização e tente novamente.
              </div>
            )}

            {locationPermissionState === "unsupported" && (
              <div
                style={{
                  background: "#fee2e2",
                  color: "#991b1b",
                  border: "1px solid #fecaca",
                  borderRadius: "14px",
                  padding: "12px 14px",
                  fontSize: "14px",
                  marginBottom: "18px",
                }}
              >
                Este dispositivo/navegador não suporta geolocalização.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
